import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { RestaurantLayout } from "@/components/tabdine/RestaurantLayout";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/tabdine/Shared";
import { X } from "lucide-react";

const FILTERS = [["all", "All"], ["active", "Active (30d)"], ["inactive", "Inactive"]];

const CustomerDetail = ({ customerId, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/restaurant/customers/${customerId}`).then((r) => setData(r.data));
  }, [customerId]);
  if (!data) return null;
  const c = data.customer;
  const orders = data.orders;
  const revenue = orders.reduce((s, o) => s + (o.status !== "cancelled" ? o.total_amount : 0), 0);
  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-brand-dark/10 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="font-heading font-bold text-brand-dark text-xl">{c.name || "Guest"}</div>
            <div className="text-xs text-brand-dark/60">{c.phone}</div>
          </div>
          <button onClick={onClose} className="text-brand-dark/60 hover:text-brand-dark"><X size={20} /></button>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-brand-light rounded-lg p-3">
              <div className="text-xs uppercase text-brand-dark/50">Orders</div>
              <div className="font-heading text-2xl font-bold text-brand-dark">{orders.length}</div>
            </div>
            <div className="bg-brand-light rounded-lg p-3">
              <div className="text-xs uppercase text-brand-dark/50">Lifetime spend</div>
              <div className="font-heading text-2xl font-bold text-brand-dark">{inr(revenue)}</div>
            </div>
          </div>
          <div className="mt-4 font-heading font-semibold text-brand-dark">Order history</div>
          <div className="mt-2 space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="bg-white border border-brand-dark/5 rounded-lg p-3 text-sm" data-testid={`cust-order-${o.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{o.order_no}</div>
                    <div className="text-xs text-brand-dark/60">
                      {(o.created_at || "").slice(0, 16).replace("T", " ")} · {o.order_type === "takeaway" ? "Takeaway" : "Dine-In"}
                      {o.table_number ? ` · T${o.table_number}` : ""}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-semibold">{inr(o.total_amount)}</div>
                    <div className={`text-xs uppercase tracking-wide ${o.status === "cancelled" ? "text-red-600" : "text-brand-orange"}`}>{o.status}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Customers() {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => {
    const url = filter === "all"
      ? `/restaurant/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`
      : `/restaurant/customers?filter=${filter}${q ? `&q=${encodeURIComponent(q)}` : ""}`;
    api.get(url).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [q, filter]);

  return (
    <RestaurantLayout>
      <div className="p-4 lg:p-8 max-w-5xl">
        <h1 className="font-heading text-2xl font-bold text-brand-dark mb-4">Customers</h1>
        <div className="flex gap-2 mb-3 flex-wrap">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or phone…" className="max-w-sm" data-testid="cust-search" />
          <div className="flex gap-2">
            {FILTERS.map(([k, l]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`cust-filter-${k}`}
                className={`px-3 py-1.5 rounded-full text-sm ${filter === k ? "bg-brand-dark text-brand-light" : "bg-white text-brand-dark/70 border border-brand-dark/10"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        {rows.length === 0 ? (
          <EmptyState title="No customers match" subtitle="Once diners place their first order, they'll show up here." />
        ) : (
          <div className="bg-white rounded-xl border border-brand-dark/5 divide-y divide-brand-dark/5">
            {rows.map((c) => (
              <button
                key={c.customer_id}
                onClick={() => setSelected(c.customer_id)}
                data-testid={`cust-row-${c.customer_id}`}
                className="w-full text-left p-3 flex items-center gap-3 hover:bg-brand-light transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-brand-orange/10 text-brand-orange grid place-items-center font-bold">
                  {(c.customer_name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-brand-dark truncate">{c.customer_name || "Guest"}</div>
                  <div className="text-xs text-brand-dark/60">{c.customer_phone || "—"}</div>
                </div>
                <div className="text-right text-xs">
                  <div className="font-medium text-brand-dark">{c.order_count} orders</div>
                  <div className="text-brand-dark/60">{inr(c.total_spent)} spent</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      {selected && <CustomerDetail customerId={selected} onClose={() => setSelected(null)} />}
    </RestaurantLayout>
  );
}
